/*
 * countdown.ts — keeps every [data-countdown] block live.
 *
 * The server renders a correct-at-request-time snapshot (so no-JS and the moment
 * before hydration both read sensibly); this only takes over ticking and the
 * state flips:  counting -> "today" (from data-target-utc) -> "done" (data-end-utc).
 * All instants are UTC ISO-8601; the browser's own clock does the wall-clock math.
 */

const UNITS = ["days", "hours", "minutes", "seconds"] as const;
type Unit = (typeof UNITS)[number];

export function initCountdown(): void {
  document.querySelectorAll<HTMLElement>("[data-countdown]").forEach(setup);
}

function setup(root: HTMLElement): void {
  const target = Date.parse(root.dataset.targetUtc ?? "");
  if (Number.isNaN(target)) return;

  const end = root.dataset.endUtc ? Date.parse(root.dataset.endUtc) : target;
  const doneText = root.dataset.textDone ?? "";
  const todayText = root.dataset.textToday ?? "";

  const timer = root.querySelector<HTMLElement>("[data-cd-timer]");
  const note = root.querySelector<HTMLElement>("[data-cd-note]");
  const values = new Map<Unit, HTMLElement>();
  UNITS.forEach((u) => {
    const el = root.querySelector<HTMLElement>(`[data-cd="${u}"]`);
    if (el) values.set(u, el);
  });

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let intervalId = 0;

  function stop(): void {
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = 0;
    }
  }

  function setValue(unit: Unit, n: number): void {
    const el = values.get(unit);
    if (!el) return;
    const text = unit === "days" ? String(n) : String(n).padStart(2, "0");
    if (el.textContent === text) return;
    el.textContent = text;
    if (!reduceMotion) {
      el.classList.remove("is-tick");
      void el.offsetWidth; // restart the animation
      el.classList.add("is-tick");
    }
  }

  function render(): void {
    const now = Date.now();

    if (now >= end) {
      root.dataset.state = "done";
      if (timer) timer.hidden = true;
      if (note) note.textContent = doneText;
      stop();
      return;
    }

    if (now >= target) {
      root.dataset.state = "today";
      if (timer) timer.hidden = true;
      if (note) note.textContent = todayText;
      return; // keep ticking until `end`
    }

    root.dataset.state = "counting";
    if (timer) timer.hidden = false;
    if (note) note.textContent = "";

    let secs = Math.floor((target - now) / 1000);
    const days = Math.floor(secs / 86400);
    secs -= days * 86400;
    const hours = Math.floor(secs / 3600);
    secs -= hours * 3600;
    const minutes = Math.floor(secs / 60);
    secs -= minutes * 60;

    setValue("days", days);
    setValue("hours", hours);
    setValue("minutes", minutes);
    setValue("seconds", secs);
  }

  render();
  intervalId = window.setInterval(() => {
    if (!document.hidden) render();
  }, 1000);

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) render();
  });
}
